import React, { useEffect, useState } from 'react';
import DiagnosticTree from '../components/DiagnosticTree/DiagnosticTree';

interface DiagnosticTreeData {
  startNode: string;
  nodes: Record<string, any>;
}

const MagicTool: React.FC = () => {
  const [treeData, setTreeData] = useState<DiagnosticTreeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [emailError, setEmailError] = useState('');

  const validateEmail = (email: string): boolean => {
    // More strict email validation
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    
    if (!emailRegex.test(email)) {
      return false;
    }
    
    // Reject common fake/test domains
    const fakeDomains = ['test.com', 'example.com', 'abc.com', 'xyz.com', 'temp.com', 'fake.com'];
    const domain = email.split('@')[1].toLowerCase();
    
    if (fakeDomains.includes(domain)) {
      return false;
    }
    
    return true;
  };

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setEmailError('Please enter your email address');
      return;
    }
    
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    
    setEmailError('');
    setEmailSubmitted(true);
    // Store email in sessionStorage for use when saving results
    sessionStorage.setItem('magicToolEmail', email);
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        // Import yaml and the data file
        const yaml = await import('js-yaml');
        const response = await fetch('/data/diagnosticTree.yaml');
        
        if (!response.ok) {
          throw new Error('Failed to load diagnostic tree data');
        }
        
        const yamlText = await response.text();
        const data = yaml.load(yamlText) as DiagnosticTreeData;
        
        setTreeData(data);
        setLoading(false);
      } catch (err) {
        console.error('Error loading diagnostic tree:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Magic Tool...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-2xl shadow-lg">
          <p className="text-red-600 mb-4">⚠️ {error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!emailSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
              🎵 Your First Song Starts Here
            </h1>
            <p className="text-gray-600">
              Enter your email to begin your personalized music creation journey
            </p>
          </div>

          <form onSubmit={handleEmailSubmit}>
            <div className="mb-6">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError('');
                }}
                className={`w-full px-4 py-3 border ${emailError ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all`}
                placeholder="your.email@example.com"
              />
              {emailError && (
                <p className="mt-2 text-sm text-red-600">{emailError}</p>
              )}
            </div>

            <button
              type="submit"
              className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              Start My Journey →
            </button>
          </form>

          <p className="mt-6 text-xs text-gray-500 text-center">
            We respect your privacy. Your email will only be used to send you personalized music creation guidance.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-12 px-4">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-8 text-center">
        <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4 pb-2">
         🎵  Your First Song Starts Here
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Answer a few questions to discover your personalized path to creating and finishing your first song.
        </p>
      </div>

      {/* Diagnostic Tree */}
      {treeData && <DiagnosticTree data={treeData} />}

      {/* Footer CTA */}
      <div className="max-w-3xl mx-auto mt-12 p-6 bg-white rounded-2xl shadow-lg text-center">
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          Need More Help?
        </h3>
        <p className="text-gray-600 mb-4">
          Join our community for personalized guidance and support
        </p>
        <button className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg">
          Join Our Community
        </button>
      </div>
    </div>
  );
};

export default MagicTool;
